import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';
import { Button, ModalYesNo } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { Switch } from '@scripsteam/scrips-signal-ds';
import { Select } from '@scripsteam/scrips-signal-ds';
import type { SelectOption } from '@scripsteam/scrips-signal-ds';
import type { ReactNode } from 'react';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { useResendInvite } from '@/settings/services';

/**
 * Plain white card without an inner header — Flutter `staff_account_tab.dart`
 * uses an outer section heading + plain card, never both. See twin helper in
 * `staff-personal-tab.tsx`.
 */
function PlainCard({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-6 py-6 flex flex-col gap-6">
      {children}
    </section>
  );
}
import {
  useArchiveStaff,
  usePracticesList,
  useRolesList,
} from '@/settings/services';
import { SpecialityPicker } from '@/settings/components/speciality-picker';
import { PasswordChangeForm } from '@/settings/shared/password-change-form';
import { DisplayPreferencesSection } from '@/settings/shared/display-preferences-section';
import type { PracticeList, RolesListItem, Staff } from '@/types/settings-api';
import {
  setStaffSpecialities,
  toggleStaffPracticeAssignment,
} from '../staff-edit-merge';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Account Details tab — PROD-765.
 *
 * Flutter source: `staff_account_tab.dart`.
 *
 * Layout (matches Flutter screenshot 2026-05-13):
 *   1. LAST SIGN-IN row (status + Resend Invite link if pending)
 *   2. ACCOUNT STATUS section card — "Set account active" toggle + helper text
 *   3. SPECIALITIES section card — SNOMED typeahead + chip pills (PROD-788).
 *      Uses the shared `SpecialityPicker` (GET /api/Master/PracticeCodeList).
 *      Add/remove routes through `setStaffSpecialities`, which keeps
 *      `specialities[]` and `assignStaffToSpecialities[]` in sync — the
 *      parallel pair the backend joins on. Persists via the staff PUT.
 *   4. ACCOUNT DETAILS section card — ROLE + EMAIL ADDRESS row,
 *      then per-practice "Assign this staff member to {name}" toggle rows.
 *
 * Email is locked once an account exists (Flutter `enabled: false` at
 * `staff_account_tab.dart:116`). On create it's editable.
 *
 * "Resend Invite" only renders when `isLastLogin == null` (the staff member
 * hasn't logged in yet, so they're presumably still in invite-pending state).
 * Wire endpoint is `POST /api/Account/ReSendInvite` with body
 * `{emailAddress, organizationId, firstName, user: "Staff"}` — verified via
 * dev cURL 2026-05-25. NOT the previously-stubbed `/api/Staff/ResendStaffInvite`
 * which never existed; that was a guess before the curl landed.
 */
export interface StaffAccountTabProps {
  draft: Staff;
  onChange: (next: Staff) => void;
  canEdit: boolean;
  isCreate: boolean;
  /**
   * Show the "Archive This Account" action (Flutter `staff_account_tab.dart:649`,
   * gated on `Manage_Staff` delete + an existing `staffSetupId`). The owning
   * page computes this — false in create mode and in Account Settings self-edit
   * (`accountMode`), where Flutter renders the simplified layout with no archive.
   */
  canArchive?: boolean;
  /** Called after a successful archive so the page can navigate away. */
  onArchived?: () => void;
  /**
   * Account Settings self-edit (Flutter `isFromAccountSettings`). When true,
   * render the SIMPLIFIED layout — Email (read-only) + password change only —
   * matching `staff_account_tab.dart:66-130`. A user editing their OWN record
   * must NOT see the account-active toggle, role, specialities, practice
   * assignments, or archive.
   */
  accountMode?: boolean;
}

export function StaffAccountTab({
  draft,
  onChange,
  canEdit,
  isCreate,
  canArchive = false,
  onArchived,
  accountMode = false,
}: StaffAccountTabProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const selfUserId = useAuthStore((s) => s.user?.userId);
  const { data: roles = [] } = useRolesList(orgId);
  const { data: practices = [] } = usePracticesList(orgId, false);

  const setField = <K extends keyof Staff>(key: K, value: Staff[K]) => {
    onChange({ ...draft, [key]: value });
  };

  // Account Settings self-edit — simplified layout (Flutter parity): the user
  // can see their email and change their password, nothing else.
  if (accountMode) {
    return (
      <div className="space-y-6">
        {/* The viewer's own display preferences, first (PM's Display card; PROD-3313). */}
        <DisplayPreferencesSection />
        <h2 className={SETTINGS_SECTION_TITLE}>
          Account Details
        </h2>
        <PlainCard>
          <div>
            <FieldLabel stacked>Email address</FieldLabel>
            <TextField type="email" value={draft.emailId} disabled readOnly />
          </div>
        </PlainCard>

        <h2 className={SETTINGS_SECTION_TITLE}>
          Password
        </h2>
        <PlainCard>
          <PasswordChangeForm userId={selfUserId} />
        </PlainCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <LastSignInRow
        isLastLogin={draft.isLastLogin}
        emailId={draft.emailId}
        firstName={draft.firstName}
        organizationId={orgId ?? ''}
        canResend={canEdit && !isCreate && !draft.isLastLogin}
      />

      <h2 className={SETTINGS_SECTION_TITLE}>
        Account Status
      </h2>
      <PlainCard>
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-default">Set account active</span>
          <Switch
            checked={draft.isActive}
            disabled={!canEdit}
            onCheckedChange={(next) => setField('isActive', next)}
            aria-label="Set account active"
          />
        </div>
        <p className="text-sm text-text-muted">
          Enabling &lsquo;Set account active&rsquo; will let the user log in into the
          system. We recommend keeping the account active at all times as long
          as the user is still a member of your organisation.
        </p>
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Specialities <span className="text-status-error">*</span>
      </h2>
      <PlainCard>
        <SpecialityPicker
          specialities={draft.specialities}
          canEdit={canEdit}
          onChange={(next) => onChange(setStaffSpecialities(draft, next))}
        />
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Account Details
      </h2>
      <PlainCard>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel stacked>
              Role <span className="text-status-error">*</span>
            </FieldLabel>
            <RoleSelect
              value={draft.role}
              options={roles}
              disabled={!canEdit}
              onChange={(next) => setField('role', next)}
            />
          </div>
          <div>
            <FieldLabel stacked>
              Email address <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              type="email"
              value={draft.emailId}
              maxLength={100}
              placeholder="staff@example.com"
              disabled={!canEdit || (!isCreate && (draft.emailId ?? '').length > 0)}
              onChange={(e) => setField('emailId', e.target.value)}
            />
            {!isCreate && (draft.emailId ?? '').length > 0 && (
              <p data-slot="settings-field-helper" className="mt-1 text-xs text-text-muted">
                Email cannot be changed after the account is created.
              </p>
            )}
          </div>
        </div>

        <PracticeAssignmentList
          draft={draft}
          practices={practices}
          canEdit={canEdit}
          onChange={onChange}
        />
      </PlainCard>

      {canArchive && draft.staffSetupId && (
        <ArchiveAccountSection
          staffSetupId={draft.staffSetupId}
          onArchived={onArchived}
        />
      )}
    </div>
  );
}

/**
 * Archive This Account — Flutter parity (`staff_account_tab.dart:642-660`).
 * Only rendered for an existing staff member with `Manage_Staff` delete
 * permission (the page gates via `canArchive`). Uses shadcn `AlertDialog`
 * for the destructive confirm — NOT `window.confirm`, which is blocked in
 * embedded WebView contexts (CONTRIBUTING recidivism log, PR #228). The
 * confirm title / message / button copy are copied verbatim from the Flutter
 * intl strings (`do_you_really_want_to_archive_this_account`,
 * `we_will_inform_the_staff_member_about_this_change_you`,
 * `archive_this_account`).
 */
function ArchiveAccountSection({
  staffSetupId,
  onArchived,
}: {
  staffSetupId: string;
  onArchived?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const archive = useArchiveStaff();

  const handleArchive = () => {
    archive.mutate(staffSetupId, {
      onSuccess: () => {
        toast.success('Account archived');
        setOpen(false);
        onArchived?.();
      },
      onError: (err) =>
        toast.error(
          err instanceof Error ? err.message : 'Could not archive this account.',
        ),
    });
  };

  return (
    <PlainCard>
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-text-muted">
          Archiving removes this staff member&rsquo;s access. You can unarchive
          the account later from the archived list.
        </p>
        <Button
          type="button"
          variant="tertiary"
          onClick={() => setOpen(true)}
          className="shrink-0 text-status-error hover:text-status-error hover:bg-status-error/10"
        >
          Archive This Account
        </Button>
      </div>

      <ModalYesNo
      open={open}
      onCancel={() => setOpen(false)}
      title="Do you really want to archive this account?"
      message="We will inform the staff member about this change. You can
              unarchive this account later in Account Settings."
      cancelLabel="Cancel"
      confirmLabel={archive.isPending ? 'Archiving…' : 'Archive This Account'}
      onConfirm={handleArchive}
      loading={archive.isPending}
    />
    </PlainCard>
  );
}

function LastSignInRow({
  isLastLogin,
  emailId,
  firstName,
  organizationId,
  canResend,
}: {
  isLastLogin: string | null;
  emailId: string;
  firstName: string;
  organizationId: string;
  canResend: boolean;
}) {
  const formatted = useMemo(() => {
    if (!isLastLogin) return 'N/A';
    const d = new Date(isLastLogin);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, [isLastLogin]);

  const resendMutation = useResendInvite();
  const canDispatch = canResend && !!emailId && !!organizationId;

  const handleResend = () => {
    if (!canDispatch) return;
    resendMutation.mutate(
      {
        emailAddress: emailId,
        organizationId,
        firstName,
        user: 'Staff',
      },
      {
        onSuccess: () => toast.success(`Invite re-sent to ${emailId}`),
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : 'Could not resend invite.',
          ),
      },
    );
  };

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm">
        <span className="font-medium text-text-secondary">
          Last sign-in:
        </span>{' '}
        <span className="text-text-default font-medium">{formatted}</span>
      </p>
      {canResend && (
        <button
          type="button"
          onClick={handleResend}
          disabled={!canDispatch || resendMutation.isPending}
          className="text-sm font-semibold text-text-default hover:text-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resendMutation.isPending ? 'Sending…' : 'Resend Invite'}
        </button>
      )}
    </div>
  );
}

// Exported (only) so it can be unit-tested directly — the DS-Select mount-time
// controlled/uncontrolled trap (see the comment inside) needs a focused test
// that doesn't have to stand up the whole StaffAccountTab + its query hooks.
export function RoleSelect({
  value,
  options,
  disabled,
  onChange,
}: {
  value: number | null;
  options: RolesListItem[];
  disabled: boolean;
  onChange: (next: number | null) => void;
}) {
  // DS Select talks in strings; coerce at the boundary. Skip archived roles
  // so a staff member can't be assigned to a hidden role.
  const usable = options.filter((r) => !r.isArchived && r.roleId != null);
  const selectOptions: SelectOption<string>[] = usable.map((r) => ({
    label: r.roleName ?? `Role ${r.roleId}`,
    value: String(r.roleId),
  }));

  // This site owns its own clear state (Archive/role reset), so it must keep
  // the DS Select CONTROLLED at all times: Base UI fixes controlled-vs-
  // uncontrolled at mount, and a `value={undefined}` mount permanently
  // ignores every later value, including a reset via `onClear`. '' is the
  // empty sentinel — it matches no option, so the trigger shows the
  // placeholder exactly as the old `value: undefined` did.
  const stringValue = value != null ? String(value) : '';

  return (
    <Select
      options={selectOptions}
      value={stringValue}
      disabled={disabled}
      placeholder="Select a role"
      clearable={!disabled}
      onChange={(next) => onChange(next ? Number(next) : null)}
      onClear={() => onChange(null)}
      aria-label="Role"
    />
  );
}

function PracticeAssignmentList({
  draft,
  practices,
  canEdit,
  onChange,
}: {
  draft: Staff;
  practices: PracticeList[];
  canEdit: boolean;
  onChange: (next: Staff) => void;
}) {
  if (practices.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No active practices to assign.
      </p>
    );
  }
  const isAssigned = (practiceId: string): boolean =>
    draft.assignStaffToPractice.find((a) => a.practiceId === practiceId)?.isAssign ??
    false;

  return (
    <div className="space-y-3 border-t border-border-hairline pt-4">
      {practices.map((p) => {
        const id = p.practiceSetupId ?? '';
        if (!id) return null;
        const checked = isAssigned(id);
        return (
          <div
            key={id}
            className="flex items-center justify-between"
          >
            <span className="text-sm text-text-default">
              Assign this staff member to {p.practiceName ?? 'Practice'}
              {p.isPrimary && (
                <span className="ml-2 text-xs font-semibold text-brand-primary">
                  Primary
                </span>
              )}
            </span>
            <Switch
              checked={checked}
              disabled={!canEdit}
              onCheckedChange={(next) =>
                onChange(toggleStaffPracticeAssignment(draft, id, next))
              }
              aria-label={`Assign this staff member to ${p.practiceName ?? 'practice'}`}
            />
          </div>
        );
      })}
    </div>
  );
}

